import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ILog } from "@ihr-radioedit/inferno-core";
import {
  PWSSessionBackend,
  RecurlyBillingInfo,
  ZypeSubscription,
  ZypeSubscriptionPlan,
  COAST_MESSAGES,
} from "@inferno/renderer-shared-core";
import { Button, ButtonKind, Heading } from "../../../../../../core/ui";
import Spinner from "../../../../../../core/ui/Spinner.component";
import { axiosErrorReason, AxiosLoaderResponse } from "../../../../lib/coastCommon";
import { canCancelSubscription, canReactivateSubscription } from "../../../../lib/coastUser";
import { FORM_ERRORS, RadioInput, RadioOption } from "../../../forms";
import { CoastAlert, CoastAlertSuccess } from "../../common/coast-alert/CoastAlert.model";
import { useCoastAlerts } from "../../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../../common/UsersButton.components";
import { CoastUserChangePlanConfirmation } from "./CoastAccountChangePlanConfirmation.component";
import { CoastUserCancelSubscription } from "./CoastUserCancelSubscription.component";
import { subscriptionPlanSubDescription } from "../../subscriptionPlanSubDescription";

const log = ILog.logger("CoastUserSubscriptionsEdit.component");

enum SubscriptionEditSubView {
  CANCEL_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
}

interface ChangePlanInputs {
  planId: string;
}

export interface ZypeSubscriptionPlanWithUpgrade extends ZypeSubscriptionPlan {
  isUpgrade: boolean;
}

interface CoastUserSubscriptionsEditProps {
  availablePlansResponse: AxiosLoaderResponse<ZypeSubscriptionPlanWithUpgrade[]>;
  updateSubscription: (s: ZypeSubscription, idx: number) => void;
  setBillingInfo: (billingInfo: RecurlyBillingInfo) => void;
  billingInfo?: RecurlyBillingInfo | null;
  billingInfoError: CoastAlert | null;
  goBack: (v?: CoastAlert[]) => void;
  subscription: ZypeSubscription;
  curSubscriptionIdx: number;
  session: PWSSessionBackend;
}

export const CoastUserSubscriptionsEdit = ({
  availablePlansResponse,
  updateSubscription,
  curSubscriptionIdx,
  subscription,
  session,
  goBack,
  ...props
}: CoastUserSubscriptionsEditProps) => {
  const canReactivate = useMemo(() => canReactivateSubscription(subscription), [subscription]);
  const canCancel = useMemo(() => canCancelSubscription(subscription), [subscription]);
  const [nextPlan, setNextPlan] = useState<ZypeSubscriptionPlanWithUpgrade | null>(null);
  const { alerts, setErrorAlert, clearAllAlerts } = useCoastAlerts();
  const [subView, setSubView] = useState<SubscriptionEditSubView | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const { handleSubmit, errors, register, watch } = useForm<ChangePlanInputs>({
    defaultValues: { planId: subscription.plan_id },
  });

  useEffect(() => {
    if (availablePlansResponse.error) {
      setErrorAlert(availablePlansResponse.error);
    }
  }, [availablePlansResponse.error, setErrorAlert]);

  const validatePlan = (v: string) => (v === subscription.plan_id ? "Current plan is selected" : true);

  const handleSelectedPlan = useCallback(
    ({ planId }: ChangePlanInputs) => {
      setNextPlan(availablePlansResponse.data?.find(p => p._id === planId) || null);
      setSubView(SubscriptionEditSubView.UPDATE_SUBSCRIPTION);
    },
    [availablePlansResponse.data],
  );

  const handleReactivation = useCallback(async () => {
    clearAllAlerts();

    if (!session.state.accessToken) {
      log.error("Error processing request: missing accessToken");
      return setErrorAlert(COAST_MESSAGES.MISSING_ACCESS_TOKEN);
    }

    setIsReactivating(true);

    try {
      const resp = await session.memberService.reactivateSubscription(session.state.accessToken, subscription._id);
      updateSubscription({ ...subscription, ...resp.subscription }, curSubscriptionIdx);
      goBack([new CoastAlertSuccess(COAST_MESSAGES.SUCCESSFUL_SUBSCRIPTION_REACTIVATE)]);
    } catch (e) {
      setIsReactivating(false);
      setErrorAlert(axiosErrorReason(e));
    }
  }, [
    clearAllAlerts,
    curSubscriptionIdx,
    goBack,
    session.memberService,
    session.state.accessToken,
    setErrorAlert,
    subscription,
    updateSubscription,
  ]);

  switch (subView) {
    case SubscriptionEditSubView.CANCEL_SUBSCRIPTION:
      return (
        <CoastUserCancelSubscription
          updateSubscription={updateSubscription}
          goBack={() => setSubView(null)}
          idx={curSubscriptionIdx}
          subscription={subscription}
          goToMainView={goBack}
          session={session}
        />
      );

    case SubscriptionEditSubView.UPDATE_SUBSCRIPTION:
      if (nextPlan) {
        return (
          <CoastUserChangePlanConfirmation
            billingInfoError={props.billingInfoError}
            setBillingInfo={props.setBillingInfo}
            billingInfo={props.billingInfo}
            updateSubscription={updateSubscription}
            goBack={() => setSubView(null)}
            subscription={subscription}
            goToMainView={goBack}
            nextPlan={nextPlan}
            session={session}
            idx={curSubscriptionIdx}
          />
        );
      }
  }

  return (
    <section className="component-account-subscriptions-edit">
      <Button className="button-back-to-account" kind={ButtonKind.CUSTOMIZED} click={() => goBack()}>
        Back to Account Settings
      </Button>

      <section className="account-sub-section">
        <Heading level={2}>Change Plan</Heading>

        <CoastAlertsDisplay alerts={alerts} />

        {availablePlansResponse.data?.length ? (
          <form onSubmit={handleSubmit(handleSelectedPlan)}>
            <RadioInput label="Select your plan" error={errors.planId} direction="vertical" name="planId">
              {availablePlansResponse.data.map(p => (
                <RadioOption
                  rules={{
                    required: FORM_ERRORS.required,
                    validate: validatePlan,
                  }}
                  error={errors.planId}
                  name="planId"
                  register={register}
                  disabled={subscription.plan_name.toLowerCase() === "coast vips"}
                  label={p.description}
                  value={p._id}
                  key={p._id}
                  subLabel={subscriptionPlanSubDescription[`${p.interval_count}_${p.interval}`] ?? ""}
                />
              ))}
            </RadioInput>

            <UsersButton
              disabled={!!errors.planId || watch("planId") === subscription.plan_id}
              label="Update"
              type="submit"
            >
              Update
            </UsersButton>
          </form>
        ) : null}

        <hr className="divider" />

        {canCancel ? (
          <section className="account-section cancel">
            <div className="content">
              <Heading level={3}>Cancel Subscription</Heading>

              <p className="cancel-subscription-help">
                Canceling your subscription allows you to access Coast Insider through the end of your current billing
                cycle. After that, you won&apos;t be charged again, but you also won&apos;t be able to access Coast
                Insider.
              </p>
            </div>

            <UsersButton
              click={() => canCancel && setSubView(SubscriptionEditSubView.CANCEL_SUBSCRIPTION)}
              disabled={!canCancel}
              label="Cancel"
            >
              Cancel Subscription
            </UsersButton>
          </section>
        ) : null}

        {canReactivate ? (
          <section className="account-section">
            <div className="content">
              <h3>Reactivate Subscription</h3>

              <p className="cancel-subscription-help">
                Re-subscribing allows you to access Coast Insider after the current billing cycle. Your subscription
                will be set to auto-renew.
              </p>
            </div>

            <Button
              click={() => canReactivate && handleReactivation()}
              kind={ButtonKind.SECONDARY}
              className="account-button"
              disabled={isReactivating}
            >
              {isReactivating ? <Spinner visible={true} /> : "Reactivate"}
            </Button>
          </section>
        ) : null}
      </section>
    </section>
  );
};
